import cx from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';
import { PopupContentFullscreen, usePopup } from '@dx-ui/osc-popup';

import { replaceLang } from '@dx-ui/framework-i18n';
import { Surface, Text } from '@dx-ui/global-ui-prism';
import { HeaderLogoLink } from './header.logo';
import {
  sendGlobalNavLinkClickGoal,
  sendGlobalNavMenuOpenGoal,
  sendInteractionReward,
} from './header.utilities';
import { Link } from '@dx-ui/osc-link';

import { GlobalNavMenuProps, TDrawerItem } from './types';
import { Icon } from '@dx-ui/osc-icon';
import { HEADER_VARIANT_TYPE } from './header.constants';

export const GlobalNavMenu: React.FC<GlobalNavMenuProps> = ({
  globalNavLinks = [],
  customClassName,
  headerVariantType,
  surfaceType,
  isTranslucent,
}) => {
  const { t } = useTranslation('osc-header');
  const { locale } = useRouter();
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  // When using enter to open popup, need delay to allow close event, otherwise popup closes instantly
  const [isCloseClickable, setIsCloseClickable] = useState(false);

  const { closePopup, setOpen } = usePopup();

  // Determine if we need inverted logo based on theme classes
  const shouldUseInvertedLogo =
    customClassName?.includes('theme-primary') ||
    customClassName?.includes('theme-secondary') ||
    surfaceType === 'inverse' ||
    surfaceType === 'contrast';

  const closeMainMenu = () => {
    if (isCloseClickable) {
      closePopup();
      setTimeout(() => {
        menuButtonRef.current?.focus();
      }, 100);
    }
  };

  const openMainMenu = () => {
    setOpen(true);
    setTimeout(() => {
      setIsCloseClickable(true);
    }, 100);
    sendGlobalNavMenuOpenGoal();
  };

  const handleLinkClick = (
    navItem: TDrawerItem | null | undefined,
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    sendInteractionReward();
    sendGlobalNavLinkClickGoal();
    navItem?.link?.onClick?.(event);
  };

  return (
    <div
      className={cx('w-full', {
        flex:
          headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal ||
          headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimalReimagine,
      })}
    >
      {headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimalReimagine ? (
        <button
          className={cx('mb-1 me-2 flex items-center gap-1', {})}
          type="button"
          id="menu-open-button"
          data-testid="hamburger-menu"
          ref={menuButtonRef}
          onClick={openMainMenu}
          aria-haspopup
        >
          <Text size="md" className="!font-bold">
            {t('mainMenu')}
          </Text>
          <span>
            <Icon name="menu" size="md" />
          </span>
        </button>
      ) : (
        <button
          className="me-2 w-12"
          type="button"
          id="menu-open-button"
          data-testid="hamburger-menu"
          ref={menuButtonRef}
          onClick={openMainMenu}
          aria-haspopup
        >
          <Icon name="menu" size="md" className="m-auto block" />
          <span className="sr-only">{t('openMenu')}</span>
        </button>
      )}
      {headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal ? (
        <div className="flex flex-col items-start justify-center gap-2 px-2">
          <HeaderLogoLink
            surfaceType={shouldUseInvertedLogo ? 'inverse' : 'base'}
            brand={{
              code: 'WW',
              url: '/',
              name: 'Hilton For the Stay',
            }}
          />
        </div>
      ) : null}

      <PopupContentFullscreen
        className={cx({
          'bg-blur': isTranslucent,
        })}
      >
        <Surface
          type={surfaceType || 'base'}
          className={cx('w-full', {
            '!bg-translucent': isTranslucent,
          })}
        >
          <button
            type="button"
            id="nav-menu-close"
            onClick={() => {
              closeMainMenu();
            }}
            ref={closeButtonRef}
            className="ms-12 mt-4"
          >
            <Icon name="close" className="m-auto" />
            <span className="text--xs font-semibold brand-ey:font-normal brand-wa:font-normal">
              <span className="sr-only">{t('closeMenu')}</span>
            </span>
          </button>
          <div className="container-fluid">
            <div className="px-2 pb-4 pt-8">
              <div className="flex items-center justify-start">
                {headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal ? (
                  <HeaderLogoLink
                    anchorClassName="mb-6 mt-2"
                    surfaceType={shouldUseInvertedLogo ? 'inverse' : 'base'}
                    brand={{
                      code: 'WW',
                      url: '/',
                      name: 'Hilton For the Stay',
                    }}
                  />
                ) : (
                  <div className="size-20">
                    <h2>
                      <HeaderLogoLink
                        surfaceType={shouldUseInvertedLogo ? 'inverse' : 'base'}
                        brand={{
                          code: 'WW',
                          url: '/',
                          name: 'Hilton For the Stay',
                        }}
                      />
                    </h2>
                  </div>
                )}
              </div>
            </div>
            {globalNavLinks.length > 0 ? (
              <div className="grid grid-cols-4 gap-x-8 gap-y-6 px-2 pb-6">
                {globalNavLinks.map((navItem, index) => (
                  <div key={`nav-section-${navItem.label || index}`} className="flex flex-col pr-6">
                    <h3 className="text--lg p-3 font-bold">
                      {navItem.link && navItem?.subMenu?.length === 0 ? (
                        <Link
                          ref={index === 0 ? firstLinkRef : undefined}
                          url={replaceLang(navItem.link.url, locale ?? 'en')}
                          anchorClassName="osc-nav-drawer-link hover:osc-nav-drawer-link-active py-3 min-w-fit focus:osc-nav-drawer-link-active block"
                          className="osc-nav-main-link  text--md font-bold no-underline"
                          onClick={(event) => handleLinkClick(navItem, event)}
                          target={navItem.link.isNewWindow ? '_blank' : undefined}
                          showNewWindowIcon={navItem.link.isNewWindow}
                        >
                          {navItem.label}
                        </Link>
                      ) : (
                        <span className="osc-nav-main-link text--md flex h-full flex-1 items-center self-stretch font-bold no-underline">
                          {navItem.label}
                        </span>
                      )}
                    </h3>
                    {navItem.subMenu && navItem.subMenu.length > 0 && (
                      <ul className="space-y-0">
                        {navItem.subMenu.map((subItem, subIndex) => {
                          if (!subItem?.link?.url) return null;
                          return (
                            <li key={`submenu-${navItem.label}-${subItem.label || subIndex}`}>
                              <span className="block w-full border-b border-surface border-b-surface-legacy hover:font-bold">
                                <Link
                                  url={replaceLang(subItem.link.url, locale ?? 'en')}
                                  anchorClassName="osc-nav-drawer-link px-3 py-4 min-w-fit hover:osc-nav-drawer-link-active focus:osc-nav-drawer-link-active brand-ey:focus:font-normal block"
                                  className="osc-nav-drawer-link-text no-underline"
                                  onClick={(event) => handleLinkClick(subItem, event)}
                                  target={subItem.link.isNewWindow ? '_blank' : undefined}
                                  showNewWindowIcon={subItem.link.isNewWindow}
                                >
                                  {subItem.label}
                                </Link>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-surface-muted">
                {t('noNavigationLinks', 'No navigation links available')}
              </div>
            )}
          </div>
        </Surface>
      </PopupContentFullscreen>
    </div>
  );
};
